import { configureStore } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice'; // Unified auth reducer
import userReducer from './slices/userSlice';
import themeReducer from './slices/themeSlice';
import languageReducer from './slices/languageSlice';
import configurationReducer from './slices/configurationSlice';
import userPreferencesReducer from './slices/userPreferencesSlice';
import notificationReducer from './slices/notificationSlice';
import tenantReducer from './slices/tenantSlice';
import apiKeyReducer from './slices/apiKeySlice';
import sysAppReducer from './slices/sysAppSlice';
import userEventLogReducer from './slices/userEventLogSlice';
import connectorReducer from './slices/connectorSlice';
import cookieConsentReducer from './slices/cookieConsentSlice';
import availableAppsReducer from './slices/availableAppsSlice';
import workspaceReducer from './slices/workspaceSlice';
import translationReducer from './slices/translationSlice';
import partyReducer from './slices/partySlice';
import packAnalysisReducer from './slices/packAnalysisSlice';
import packAnalysisWorkbenchReducer from './slices/packAnalysisWorkbenchSlice';
import countryCodeReducer from './slices/countryCodeSlice';
import unLocodeReducer from './slices/unLocodeSlice';
import iataCodeReducer from './slices/iataCodeSlice';
import chatReducer from './slices/chatSlice';
import chatSessionReducer from './slices/chatSessionSlice';
import chatUserMemoryReducer from './slices/chatUserMemorySlice';
import chatPreferencesReducer from './slices/chatPreferencesSlice';
import uiContextReducer from './slices/uiContextSlice';
import brandingReducer from './slices/brandingSlice';
import userAppAccessReducer from './slices/userAppAccessSlice';
import itemMasterReducer from './slices/itemMasterSlice';
import companyProfileReducer from './slices/companyProfileSlice';
import uomLadderReducer from './slices/uomLadderSlice';
import packagingRuleReducer from './slices/packagingRuleSlice';
import ediFieldMappingReducer from './slices/ediFieldMappingSlice';
import lineDisplayProfileReducer from './slices/lineDisplayProfileSlice';
import ssccReducer from './slices/ssccSlice';
import cartonTypeCatalogReducer from './slices/cartonTypeCatalogSlice';
import keyCompositionReducer from './slices/keyCompositionSlice';
import userPartyAssignmentReducer from './slices/userPartyAssignmentSlice';
import userAffiliationTypeReducer from './slices/userAffiliationTypeSlice';
import activePartyReducer from './slices/activePartySlice';
import speechReducer from './slices/speechSlice';
import poWorkbenchReducer from './slices/poWorkbenchSlice';
import appNavReducer from './slices/appNavSlice';
import workspacePermissionsReducer from './slices/workspacePermissionsSlice';
import { tenantPersistListener } from './middleware/tenantPersistListener';
import { activePartyPersistListener } from './middleware/activePartyPersistListener';

const store = configureStore({
  reducer: {
    auth: authReducer, // Unified auth system (renamed from newAuth)
    user: userReducer,
    theme: themeReducer,
    language: languageReducer,
    configuration: configurationReducer,
    userPreferences: userPreferencesReducer,
    notification: notificationReducer,
    tenant: tenantReducer,
    apiKey: apiKeyReducer,
    sysApp: sysAppReducer,
    userEventLog: userEventLogReducer,
    connector: connectorReducer,
    cookieConsent: cookieConsentReducer,
    availableApps: availableAppsReducer,
    workspace: workspaceReducer,
    translation: translationReducer,
    party: partyReducer,
    packAnalysis: packAnalysisReducer,
    packAnalysisWorkbench: packAnalysisWorkbenchReducer,
    poWorkbench: poWorkbenchReducer,
    countryCode: countryCodeReducer,
    unLocode: unLocodeReducer,
    iataCode: iataCodeReducer,
    chat: chatReducer,
    chatSession: chatSessionReducer,
    chatUserMemory: chatUserMemoryReducer,
    chatPreferences: chatPreferencesReducer,
    uiContext: uiContextReducer,
    branding: brandingReducer,
    userAppAccess: userAppAccessReducer,
    itemMaster: itemMasterReducer,
    companyProfile: companyProfileReducer,
    uomLadder: uomLadderReducer,
    packagingRule: packagingRuleReducer,
    ediFieldMapping: ediFieldMappingReducer,
    lineDisplayProfile: lineDisplayProfileReducer,
    sscc: ssccReducer,
    cartonTypeCatalog: cartonTypeCatalogReducer,
    keyComposition: keyCompositionReducer,
    userPartyAssignment: userPartyAssignmentReducer,
    userAffiliationType: userAffiliationTypeReducer,
    activeParty: activePartyReducer,
    speech: speechReducer,
    appNav: appNavReducer,
    workspacePermissions: workspacePermissionsReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for serialization checks
        ignoredActions: ['auth/updateLoadingStates'], // Updated action type prefix
      },
    })
      // Prepend so the listener middleware runs before defaults (sees actions
      // before serializableCheck etc.). tenantPersistListener persists
      // activeTenantId whenever setActiveTenant fires (closes the F5-tenant-lost
      // bug); activePartyPersistListener persists the active buyer/supplier
      // whenever setActiveBuyer/setActiveSupplier fires. See each file for notes.
      .prepend(tenantPersistListener.middleware)
      .prepend(activePartyPersistListener.middleware),
});

// Inject store into apiClient so it can read auth + tenant state inside its
// interceptors without apiClient having to top-level-import store itself.
// apiClient no longer imports store at runtime — that closed the circular
// dependency (apiClient → store → slices → thunks → services → apiClient) that
// Jest's strict module-resolution can't unwind. The one-way dependency now
// flows store → apiClient via this setter; apiClient.getStoreState() throws
// loud if any code calls it before this line runs.
import { setApiClientStore } from '../utils/apiClient';
// Guard against jest.mock(apiClient) replacing the module without the setter —
// 5 auth test files mock apiClient as `{ apiClient: ... }` only. In that case
// `setApiClientStore` resolves to undefined; we silently skip rather than
// crash, since the mocked apiClient doesn't need a store reference anyway.
if (typeof setApiClientStore === 'function') {
  setApiClientStore(store);
}

// Make store available in browser console for debugging
if (typeof window !== 'undefined') {
  (window as any).store = store;
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export { store };
export default store;
